import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Chip, ChipProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ReactElement } from 'react';

interface CustomChipsProps extends ChipProps {
  filledPrimary?: boolean;
  filledNeutral?: boolean;
  infoOrange?: boolean;
  infoBlue?: boolean;
  successGreen?: boolean;
  warningOrange?: boolean;
  errorRed?: boolean;
  isDisabled?: boolean;
  textContent?: string;
  icon?: ReactElement;
  statusChips?: boolean;
}

const getChipStyles = (props: CustomChipsProps) => {
  const { filledPrimary, filledNeutral, infoOrange, infoBlue, successGreen, warningOrange, errorRed } = props;

  let backgroundColor = 'transparent';
  let backgroundColorHover = '';
  let backgroundColorPressed = '';
  let textColor = tsoPqTheme.palette.customTextColor?.color_font_ft_second_text;

  if (filledPrimary) {
    backgroundColor = tsoPqTheme.palette.background.primary.color_primary_background_bg_prim_enabled_lt;
    backgroundColorHover = tsoPqTheme.palette.background.primary?.color_primary_background_bg_prim_hover_lt;
    backgroundColorPressed = tsoPqTheme.palette.background.primary?.color_primary_background_bg_prim_pressed_lt;
  } else if (filledNeutral) {
    backgroundColor = tsoPqTheme.palette.background.neutral?.color_neutral_background_bg_ntrl_enabled_lt;
    backgroundColorHover = tsoPqTheme.palette.background.neutral?.color_neutral_background_bg_ntrl_hover_lt;
    backgroundColorPressed = tsoPqTheme.palette.background.neutral?.color_neutral_background_bg_ntrl_pressed_lt;
  } else if (infoOrange) {
    backgroundColor = tsoPqTheme.palette.customInformation.color_signal_warning_warning_bg_light;
    textColor = tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled;
    backgroundColorHover = tsoPqTheme.palette.customInformation.color_signal_warning_warning_bg_light;
    backgroundColorPressed = tsoPqTheme.palette.customInformation.color_signal_warning_warning_bg_light;
  } else if (infoBlue) {
    backgroundColor = tsoPqTheme.palette.customInformation.color_signal_information_information_bg_accent;
    textColor = tsoPqTheme.palette.customTextColor.color_neutral_font_ft_nrtrl_enabled;
    backgroundColorHover = tsoPqTheme.palette.customInformation.color_signal_information_information_bg_accent;
    backgroundColorPressed = tsoPqTheme.palette.customInformation.color_signal_information_information_bg_accent;
  } else if (successGreen) {
    backgroundColor = tsoPqTheme.palette.customInformation.color_signal_success_success_bg_accent;
    textColor = tsoPqTheme.palette.customInformation.color_signal_success_success;
    backgroundColorHover = tsoPqTheme.palette.customInformation.color_signal_success_success_bg_accent;
    backgroundColorPressed = tsoPqTheme.palette.customInformation.color_signal_success_success_bg_accent;
  } else if (warningOrange) {
    backgroundColor = tsoPqTheme.palette.customInformation.color_signal_warning_warning_bg_accent;
    textColor = tsoPqTheme.palette.customInformation.color_signal_warning_warning;
    backgroundColorHover = tsoPqTheme.palette.customInformation.color_signal_warning_warning_bg_accent;
    backgroundColorPressed = tsoPqTheme.palette.customInformation.color_signal_warning_warning_bg_accent;
  } else if (errorRed) {
    backgroundColor = tsoPqTheme.palette.customError.color_signal_error_error_bg_light;
    textColor = tsoPqTheme.palette.customError.color_signal_error_error;
    backgroundColorHover = tsoPqTheme.palette.customError.color_signal_error_error_bg_light;
    backgroundColorPressed = tsoPqTheme.palette.customError.color_signal_error_error_bg_light;
  }

  return {
    backgroundColor,
    backgroundColorHover,
    backgroundColorPressed,
    textColor,
  };
};

const StyledChip = styled(Chip, {
  shouldForwardProp: (prop) =>
    ![
      'filledPrimary',
      'filledNeutral',
      'infoOrange',
      'infoBlue',
      'successGreen',
      'warningOrange',
      'errorRed',
      'isDisabled',
      'textContent',
      'icon',
      'validation',
    ].includes(prop as string),
})<CustomChipsProps>((props) => {
  const { backgroundColor, textColor, backgroundColorHover, backgroundColorPressed } = getChipStyles(props);

  return {
    backgroundColor,
    backgroundColorHover,
    backgroundColorPressed,
    color: textColor,
    fontFamily: tsoPqTheme.typography.openSans,
    fontSize: tsoPqTheme.typography.fontsize_button_button_small,
    lineHeight: props.statusChips ? '1.125rem' : '1.25rem',
    letterSpacing: '0.007rem',
    fontWeight: '600',
    fontStyle: 'normal',
    height: props.statusChips ? '1.125rem' : '2rem',
    minWidth: '3rem',
    textTransform: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: props.statusChips
      ? `${tsoPqTheme.spacingValues.atomSpacing.spacing_atom_padding_atom_padding_m} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_4}`
      : `${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_4} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_4} ${tsoPqTheme.spacingValues.componentSpacing.spacing_component_padding_8}`,
    gap: props.statusChips
      ? tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8
      : tsoPqTheme.spacingValues.buttonSpacing.spacing_button_small_button_sm_gap,
    borderRadius: tsoPqTheme.shape.border_radius_tso_border_radius_4,
    '&.active': {
      backgroundColor,
      color: textColor,
      transition: 'background-color 150ms ease-out',
    },
    '&:hover': {
      backgroundColor: backgroundColorHover,
      color: textColor,
      cursor: props.filledPrimary || props.filledNeutral ? 'pointer' : 'default',
    },

    '& .MuiChip-label': {
      padding: '0',
    },
  };
});

const StyledChipContent = styled(Box)({
  display: 'flex',
  gap: tsoPqTheme.spacingValues.componentSpacing.spacing_component_gap_8,
});

const StyledIcon = styled(FontAwesomeIcon)({
  width: '1.125rem',
  height: '1.125rem',
  color: tsoPqTheme.palette.customTextColor?.color_font_ft_second_text,
});

const ChipTsoPq = ({ textContent, icon, ...props }: CustomChipsProps): ReactElement => (
  <StyledChip
    {...props}
    label={
      <StyledChipContent>
        {textContent}
        {icon && <StyledIcon icon={faChevronDown} />}
      </StyledChipContent>
    }
    onClick={() => {}}
  />
);

export default ChipTsoPq;
